import React, { useState } from "react";
import { useI18n } from "@/i18n";
import { useCookieConsent } from "@/context/CookieConsentContext";
import { Cookie, X } from "lucide-react";

export default function CookieBanner() {
  const { t } = useI18n();
  const { showBanner, showPreferences, openPreferences, closePreferences, acceptAll, rejectAll, savePreferences, consent } = useCookieConsent();
  const [prefs, setPrefs] = useState({
    analytics: consent.analytics,
    marketing: consent.marketing,
    personalization: consent.personalization,
  });

  React.useEffect(() => {
    setPrefs({
      analytics: consent.analytics,
      marketing: consent.marketing,
      personalization: consent.personalization,
    });
  }, [consent, showPreferences]);

  if (!showBanner && !showPreferences) return null;

  const toggle = (key) => setPrefs((p) => ({ ...p, [key]: !p[key] }));

  return (
    <>
      {showBanner && !showPreferences && (
        <div className="cookie-banner" role="dialog" aria-live="polite" aria-label={t("cookieBanner.title")} data-testid="cookie-banner">
          <div className="cookie-banner-inner">
            <div className="cookie-banner-copy">
              <div className="cookie-banner-header">
                <Cookie size={18} strokeWidth={1.8} />
                <h3>{t("cookieBanner.title")}</h3>
              </div>
              <p>{t("cookieBanner.body")}</p>
            </div>
            <div className="cookie-banner-actions">
              <button className="btn btn-ghost" onClick={rejectAll} data-testid="cookie-reject">{t("cookieBanner.rejectAll")}</button>
              <button className="btn btn-outline" onClick={openPreferences} data-testid="cookie-manage">{t("cookieBanner.manage")}</button>
              <button className="btn btn-primary" onClick={acceptAll} data-testid="cookie-accept">{t("cookieBanner.acceptAll")}</button>
            </div>
          </div>
        </div>
      )}

      {showPreferences && (
        <div className="cookie-modal-backdrop" role="dialog" aria-modal="true" data-testid="cookie-modal">
          <div className="cookie-modal">
            <div className="cookie-modal-head">
              <h3>{t("cookieBanner.manage")}</h3>
              <button className="icon-btn" onClick={closePreferences} aria-label="close">
                <X size={18} />
              </button>
            </div>
            <p className="cookie-modal-body">{t("cookieBanner.body")}</p>

            <ul className="cookie-categories">
              <li>
                <div>
                  <strong>{t("cookieBanner.category.necessary")}</strong>
                  <span>{t("cookieBanner.category.necessaryDesc")}</span>
                </div>
                <label className="switch is-locked"><input type="checkbox" checked readOnly /><span /></label>
              </li>
              <li>
                <div>
                  <strong>{t("cookieBanner.category.analytics")}</strong>
                  <span>{t("cookieBanner.category.analyticsDesc")}</span>
                </div>
                <label className="switch"><input type="checkbox" checked={prefs.analytics} onChange={() => toggle("analytics")} data-testid="pref-analytics" /><span /></label>
              </li>
              <li>
                <div>
                  <strong>{t("cookieBanner.category.marketing")}</strong>
                  <span>{t("cookieBanner.category.marketingDesc")}</span>
                </div>
                <label className="switch"><input type="checkbox" checked={prefs.marketing} onChange={() => toggle("marketing")} data-testid="pref-marketing" /><span /></label>
              </li>
              <li>
                <div>
                  <strong>{t("cookieBanner.category.personalization")}</strong>
                  <span>{t("cookieBanner.category.personalizationDesc")}</span>
                </div>
                <label className="switch"><input type="checkbox" checked={prefs.personalization} onChange={() => toggle("personalization")} data-testid="pref-personalization" /><span /></label>
              </li>
            </ul>

            <div className="cookie-modal-actions">
              <button className="btn btn-ghost" onClick={rejectAll} data-testid="cookie-modal-reject">{t("cookieBanner.rejectAll")}</button>
              <button className="btn btn-primary" onClick={() => savePreferences(prefs)} data-testid="cookie-save">{t("cookieBanner.save")}</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
